import React, { useEffect, useState } from "react";
import { Button, Col, Row, Stack } from "react-bootstrap";
import { useParams } from "react-router-dom";
import {
  fetchBeds,
  fetchEthnicities,
  fetchGenders,
  fetchJobs,
  fetchPrograms,
  fetchRaces,
  fetchRelationships,
  fetchRooms,
  patchBed,
  patchFieldValue,
  patchMember,
  postFieldValue,
} from "../../../../services/httpClient";
import DatePicker from "../../../ui/DatePicker";
import Input from "../../../ui/Input";
import InputPhone from "../../../ui/InputPhone";
import SelectObject from "../../../ui/SelectObject";
import ConfirmDelete from "./ConfirmDelete";
import ConfirmBan from "../../../ui/ConfirmBan";
import useApp from "../../../../utils/useApp";
import SelectObjectTypeahead from "../../../ui/SelectObjectTypeahead";
import CustomField from "../../../ui/CustomField";
import CaptureProfilePic from "../../../ui/CaptureProfilePic";

interface EditMemberState {
  user?: any;
  family: any;
  fetchHousehold: any;
  resetHouseholdTab: any;
  type?: string;
  currentCase?: any;
  setUserToSave: any;
  onClickDoneEdit: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
  myCase: boolean;
  caseStatus: string;
}

const EditMember = ({
  user,
  family,
  fetchHousehold,
  resetHouseholdTab,
  type,
  currentCase,
  setUserToSave,
  onClickDoneEdit,
  setShowToast,
  setToastText,
  setToastBody,
  myCase,
  caseStatus,
}: EditMemberState) => {
  const { id } = useParams();
  const { auth }: any = useApp();
  const heading = type === "Child" ? "Edit Child" : "Edit Adult";
  const [memberId, setMemberId] = useState<string>();
  const [firstName, setFirstName] = useState<string | undefined>("");
  const [lastName, setLastName] = useState<string>("");
  const [preferredName, setPreferredName] = useState<string>("");
  const [phone, setPhone] = useState<number | null>(null);
  const [relationship, setRelationship] = useState<string>("");
  const [intakeDate, setIntakeDate] = useState<string>();
  const [exitDate, setExitDate] = useState<string>();
  const [birthDate, setBirthDate] = useState<string>("");
  const [school, setSchool] = useState<string>("");
  const [grade, setGrade] = useState<string>("");
  const [gender, setGender] = useState<string>("");
  const [race, setRace] = useState<string>("");
  const [ethnicity, setEthnicity] = useState<string>("");
  const [program, setProgram] = useState<any>();
  const [level, setLevel] = useState<any>(null);
  const [room, setRoom] = useState<any>();
  const [previousBed, setPreviousBed] = useState<any>();
  const [bed, setBed] = useState<any>();
  const [previousJob, setPreviousJob] = useState<string>("");
  const [job, setJob] = useState<any>();
  const [custom, setCustom] = useState<any[]>([]);
  const [isBanned, setIsBanned] = useState<boolean>(false);
  const [profileImage, setProfileImage] = useState<string>("");
  const [saveIsEnabled, setSaveIsEnabled] = useState<boolean>(false);
  const [isValidPhone, setIsValidPhone] = useState<boolean>(true);
  const [errMsg, setErrMsg] = useState("");
  const [relationships, setRelationships] = useState<any[]>([]);
  const [genders, setGenders] = useState<any[]>([]);
  const [races, setRaces] = useState<any[]>([]);
  const [ethnicities, setEthnicities] = useState<any[]>([]);
  const [programs, setPrograms] = useState<any[]>([]);
  const [levels, setLevels] = useState<any[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [rooms, setRooms] = useState<any[]>([]);
  const [beds, setBeds] = useState<any[]>([]);
  const [filteredBeds, setFilteredBeds] = useState<any[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState<string>();
  const [isBanning, setIsBanning] = useState(false);
  const [memberToBan, setMemberToBan] = useState<string>();
  const [progLoading, setProgLoading] = useState<boolean>(true);
  const [updated, setUpdated] = useState<boolean>(false);

  useEffect(() => {
    getRelationships();
    getGenders();
    getRaces();
    getEthnicities();

    getPrograms();
    getJobs();
    if (auth?.profile.includeHMIS === 1) {
      getBeds();
    }
  }, [user]);

  const getRelationships = () => {
    fetchRelationships().then((res: any) => {
      setRelationships(res[0].data);
    });
  };

  const getGenders = () => {
    fetchGenders().then((res: any) => {
      setGenders(res[0].data);
    });
  };

  const getRaces = () => {
    fetchRaces().then((res: any) => {
      setRaces(res[0].data);
    });
  };

  const getEthnicities = () => {
    fetchEthnicities().then((res: any) => {
      setEthnicities(res[0].data);
    });
  };

  const getPrograms = () => {
    setProgLoading(true);

    fetchPrograms()
      .then((res: any) => {
        let programs = res[0].data;
        const updatedPrograms = [{ id: 0, key: 0, name: "Select from List", programLevels: [] }, ...programs];
        setPrograms(updatedPrograms);
        setLevels([]);

        const selectedProgram = updatedPrograms.find((p: any) => p.key === user?.program);
        if (selectedProgram) {
          if (selectedProgram.programLevels && selectedProgram.programLevels.length > 0) {
            setLevels(selectedProgram.programLevels);
            if (user?.level_id) {
              const selectedLevel = selectedProgram.programLevels.find((lvl: any) => lvl.id === user.level_id);
              if (selectedLevel) {
                setLevel(selectedLevel);
              } else if (level !== null) {
                setLevel(null);
              }
            }
          }
          setProgram(selectedProgram);
        }
        setSaveIsEnabled(false);
        setUserToSave(false);
      })
      .finally(() => {
        setProgLoading(false);
      });
  };

  const getJobs = async () => {
    fetchJobs().then((res: any) => {
      const jobs = res[0].data;
      var availJobs = jobs.filter((j: any) => j.positions_filled < j.positions_total);

      //Ensure user's job is included
      const userJob = jobs.find((j: any) => j.key === user?.job);

      if (userJob && !availJobs.some((j: any) => j.id === userJob.id)) {
        availJobs.push(userJob);
      }

      availJobs = [{ id: 0, key: 0, name: "Select from List" }, ...availJobs];
      setJobs(availJobs);

      const selectedJob = availJobs.find((j: any) => j.key === user?.job);
      if (selectedJob) {
        setJob(selectedJob);
      } else if (user?.job) {
        const currentJob = jobs.find((j: any) => j.key === user?.job);
        setJob(currentJob);
        setJobs((prevJobs) => [currentJob, ...prevJobs]);
      } else {
        setJob({ key: "", name: "" });
      }
    });
  };

  const getBeds = () => {
    fetchBeds()
      .then((res: any) => {
        var bedList = res[0].data;
        //include bed user is currently assigned to
        var currentBed;
        if (user?.bed) {
          currentBed = bedList.find((b: any) => b.key === user?.bed);
        }
        //filter to vacant beds
        bedList = bedList.filter((b: any) => b.occupant_id === null);

        if (currentBed) {
          //if user is assigned to a bed, add it to the list
          bedList.push(currentBed);
        }

        bedList.sort((a: any, b: any) => {
          // group by room name
          if (a.room_name !== b.room_name) {
            return a.room_name.localeCompare(b.room_name, undefined, { numeric: true, sensitivity: "base" });
          }
          // sort beds in each room alphanumerically
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });

        setBeds(bedList);
        if (user?.room) {
          const filtered = bedList.filter((b: any) => b.room_key === user?.room);
          setFilteredBeds(filtered);
        } else {
          setFilteredBeds(bedList);
        }
        getRooms();
      })
      .catch((err: any) => {
        console.log(err);
      })
      .finally(() => {
        setErrMsg("");
      });
  };

  const getRooms = () => {
    fetchRooms()
      .then((res: any) => {
        const roomList = res[0].data;
        roomList.sort((a: any, b: any) => {
          return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" });
        });
        setRooms(roomList);
      })
      .catch((err: any) => {
        console.log(err);
      });
  };

  useEffect(() => {
    setMemberId(user?.id);
    setFirstName(user?.firstname);
    setLastName(user?.lastname);
    setPreferredName(user?.preferredname);
    setPhone(user?.phonenumber === "" ? null : user?.phonenumber);
    setRelationship(user?.relationship);
    setIntakeDate(user?.intake_at);
    setExitDate(user?.exit_at);
    setBirthDate(user?.birthdate);
    setSchool(user?.school);
    setGrade(user?.grade);
    setGender(user?.gender);
    setRace(user?.race);
    setEthnicity(user?.ethnicity);
    setPreviousJob(user?.job);
    setIsBanned(user?.is_banned === 1 ? true : false);

    if (user?.profile.length > 0) {
      user?.profile?.map((p: any) => {
        const base64String = "data:image/jpeg;base64, " + p?.data;
        setProfileImage(base64String);
      });
    } else {
      setProfileImage("");
    }

    //HMIS ONLY
    if (auth?.profile.includeHMIS === 1) {
      setRoom(user?.room);
      setPreviousBed(user?.bed);
      setBed(user?.bed);
    }
  }, [user]);

  function validatePhone(phone: string) {
    let length = phone.length;
    return length === 11;
  }

  function enableSave() {
    if (firstName && lastName && isValidPhone && checkProgram()) {
      setSaveIsEnabled(true);
      setUserToSave(true);
    } else {
      setSaveIsEnabled(false);
      setUserToSave(false);
    }
  }

  const checkProgram = () => {
    if (!program) return true;
    if (levels?.length > 0 && !level) {
      return false;
    }
    return true;
  };

  const saveMember = () => {
    setErrMsg("");

    if (firstName && lastName && isValidPhone) {
      const updatedMember = {
        firstname: firstName,
        lastname: lastName,
        preferredname: preferredName,
        phonenumber: phone,
        relationship: relationship,
        intake_at: intakeDate,
        exit_at: exitDate,
        birthdate: birthDate,
        gender: gender,
        race: race,
        ethnicity: ethnicity,
        program: program?.key === 0 ? 0 : program?.key ? program.key : null,
        level_id: level?.id === 0 ? 0 : level?.id ? level.id : null,
        previous_job: previousJob,
        job: job?.key === 0 ? 0 : job?.key ? job.key : null,
        school: school,
        grade: grade,
      };

      if (updatedMember.program === 0) {
        updatedMember.level_id = null;
      }

      patchMember(updatedMember, id, memberId)
        .then((res: any) => {
          setPreviousJob(updatedMember?.job);
          setSaveIsEnabled(false);
          setUserToSave(false);
          fetchHousehold();
          setShowToast(true);
          setToastText("Member Updated");
          const toast = preferredName ? firstName + " '" + preferredName + "' " + lastName : firstName + " " + lastName;
          setToastBody(toast);
        })
        .catch((err: any) => {
          setErrMsg(err?.response?.data?.msg);
        });

      if (auth?.profile.includeHMIS === 1) {
        if (bed != user?.bed) {
          //setting bed vacant
          if (bed == 0) {
            const updatedBed = {
              occupant_id: bed,
            };

            patchBed(updatedBed, previousBed)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          } else if (bed) {
            const updatedBed = {
              occupant_id: user.id,
              occupant_type: "member",
            };

            patchBed(updatedBed, bed)
              .then((res: any) => {})
              .catch((err: any) => {
                setErrMsg(err?.response?.data?.msg);
              });
          }
        }
      }

      //update / create custom field values
      custom.map((cust: any) => {
        if (cust?.fieldValueId) {
          //patch
          const fieldVal = {
            value: cust?.value,
          };

          patchFieldValue(fieldVal, cust?.fieldValueId).catch((err: any) => {
            console.log(err);
            setErrMsg(err?.response?.data?.msg);
          });
        } else {
          //post
          const fieldVal = {
            field_id: cust?.fieldId,
            person_id: user?.id,
            person_type: "member",
            value: cust?.value,
          };

          postFieldValue(fieldVal).catch((err: any) => {
            console.log(err);
            setErrMsg(err?.response?.data?.msg);
          });
        }
      });
    } else if (!firstName || !lastName) {
      setErrMsg("First and Last name are required");
    } else {
      setErrMsg("Phone is in Invalid Format");
    }
  };

  const onClickDelete = () => {
    setIsDeleting(true);
    setMemberToDelete(firstName + " " + lastName);
  };

  const updateFirstName = (e: any) => {
    setFirstName(e.target.value);
    enableSave();
  };

  const updateLastName = (e: any) => {
    setLastName(e.target.value);
    enableSave();
  };

  const updatePreferredName = (e: any) => {
    setPreferredName(e.target.value);
    enableSave();
  };

  const updatePhone = (e: any) => {
    setPhone(e);

    let validPhone = validatePhone(e);
    if (validPhone) {
      setIsValidPhone(true);
      enableSave();
    } else {
      if (e === "1") {
        setIsValidPhone(true);
        enableSave();
      } else {
        setIsValidPhone(false);
      }
    }
  };

  const updateRelationship = (e: any) => {
    setRelationship(e.target.value);
    enableSave();
  };

  const updateIntakeDate = (e: any) => {
    setIntakeDate(e);
    enableSave();
  };

  const updateBirthDate = (e: any) => {
    if (e) {
      setBirthDate(e);
    } else {
      setBirthDate("");
    }
    enableSave();
  };

  const updateSchool = (e: any) => {
    setSchool(e.target.value);
    enableSave();
  };

  const updateGrade = (e: any) => {
    setGrade(e.target.value);
    enableSave();
  };

  const updateGender = (e: any) => {
    setGender(e.target.value);
    enableSave();
  };

  const updateRace = (e: any) => {
    setRace(e.target.value);
    enableSave();
  };

  const updateEthnicity = (e: any) => {
    setEthnicity(e.target.value);
    enableSave();
  };

  const updateProgram = (e: any) => {
    setUpdated(true);
    setLevel(null);
    setLevels([]);
    if (e.programLevels && e.programLevels.length > 0) {
      setLevels(e.programLevels);
    }
    setProgram(e);
  };

  const updateLevel = (e: any) => {
    setUpdated(true);
    if (e.key !== level?.key) {
      setLevel(e);
    }
  };

  useEffect(() => {
    if (progLoading) return;
    if (!updated) return;
    enableSave();
  }, [program, level, progLoading]);

  const updateJob = (e: any) => {
    if (e.key !== job.key) {
      enableSave();
    }
    setJob(e);
  };

  const updateRoom = (e: any) => {
    setRoom(e.target.value);
    setBed(null);
    const filtered = beds.filter((b: any) => b.room_key === e.target.value);
    setFilteredBeds(filtered);
  };

  const updateBed = (e: any) => {
    setPreviousBed(bed);
    setBed(e.target.value);
    enableSave();
  };

  const onClickBan = () => {
    setIsBanning(true);
    setMemberToBan(firstName + " " + lastName);
  };

  return (
    <>
      {isDeleting ? (
        <ConfirmDelete
          userId={memberId}
          setIsDeleting={setIsDeleting}
          userToDelete={memberToDelete}
          setUserToDelete={setMemberToDelete}
          fetchHousehold={fetchHousehold}
          resetHouseholdTab={resetHouseholdTab}
          type="Member"
          caseStatus={currentCase?.casestatus}
          family={family}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : isBanning ? (
        <ConfirmBan
          user={user}
          previousJob={previousJob}
          isBanned={isBanned}
          setIsBanning={setIsBanning}
          userToBan={memberToBan}
          setUserToBan={setMemberToBan}
          fetchHousehold={fetchHousehold}
          resetCurrentView={resetHouseholdTab}
          type="Member"
          caseStatus={caseStatus}
          setShowToast={setShowToast}
          setToastText={setToastText}
          setToastBody={setToastBody}
        />
      ) : (
        <div>
          <Stack gap={3} className="mb-3">
            <Row>
              <Col>
                <h4 className="app-header-text">{heading}</h4>
              </Col>
              <Col className="text-end">
                <Stack className="stack-float" direction="horizontal" gap={3}>
                  {myCase && (
                    <>
                      <Button className="app-danger-color button" onClick={() => onClickDelete()}>
                        Delete
                      </Button>
                      <div className="vr" />
                      <Button className="app-secondary-color button" onClick={() => saveMember()} disabled={!saveIsEnabled}>
                        Save
                      </Button>
                      <div className="vr" />
                    </>
                  )}
                  <Button className="app-primary-color button" onClick={() => onClickDoneEdit()}>
                    Done
                  </Button>
                </Stack>
              </Col>
            </Row>
            <p className={errMsg ? "errmsg" : "offscreen"} aria-live="assertive">
              {errMsg}
            </p>
            <Stack gap={3}>
              <Row className="h-auto">
                <Col xs={4}>
                  <Input
                    label="First Name"
                    type="text"
                    value={firstName}
                    onUpdate={(e: any) => updateFirstName(e)}
                    required={true}
                    disabled={myCase ? false : true}
                  />
                </Col>
                <Col xs={4}>
                  <Input
                    label="Last Name"
                    type="text"
                    value={lastName}
                    onUpdate={(e: any) => updateLastName(e)}
                    required={true}
                    disabled={myCase ? false : true}
                  />
                </Col>
                <Col xs={4}>
                  <Input
                    label="Preferred Name"
                    type="text"
                    value={preferredName}
                    onUpdate={(e: any) => updatePreferredName(e)}
                    disabled={myCase ? false : true}
                  />
                </Col>
              </Row>
              <Row className="h-auto">
                <Col xs={8}>
                  <Stack gap={3}>
                    <Row>
                      <Col xs={6}>
                        <InputPhone label="Phone" value={phone} onUpdate={(e: any) => updatePhone(e)} disabled={myCase ? false : true} />
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={6}>
                        <DatePicker
                          label="Intake Date"
                          currentValue={intakeDate}
                          setCurrentValue={updateIntakeDate}
                          hasMaxDate={true}
                          disabled={currentCase?.casestatus === "active" && myCase ? false : true}
                        />
                      </Col>
                      {currentCase?.casestatus !== "active" && (
                        <Col xs={6}>
                          <DatePicker
                            label="Exit Date"
                            currentValue={exitDate}
                            setCurrentValue={setExitDate}
                            hasMaxDate={true}
                            disabled={true}
                          />
                        </Col>
                      )}
                      <Col className={currentCase?.casestatus !== "active" ? "col-6 mt-3" : "col-6"}>
                        <DatePicker
                          label="Birth Date"
                          currentValue={birthDate}
                          setCurrentValue={updateBirthDate}
                          hasMaxDate={true}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="col-6">
                        <SelectObject
                          label="Relationship"
                          value={relationship}
                          options={relationships}
                          onUpdate={(e: any) => updateRelationship(e)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col className="col-6">
                        <SelectObject
                          label="Gender"
                          value={gender}
                          options={genders}
                          onUpdate={(e: any) => updateGender(e)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                    <Row>
                      <Col className="col-6">
                        <SelectObject
                          label="Ethnicity"
                          value={ethnicity}
                          options={ethnicities}
                          onUpdate={(e: any) => updateEthnicity(e)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                      <Col className="col-6">
                        <SelectObject
                          label="Race"
                          value={race}
                          options={races}
                          onUpdate={(e: any) => updateRace(e)}
                          disabled={myCase ? false : true}
                        />
                      </Col>
                    </Row>
                  </Stack>
                </Col>
                <Col xs={4}>
                  <CaptureProfilePic
                    userId={user?.id}
                    userProfileId={user?.profile_id}
                    userName={firstName + " " + lastName}
                    userType="member"
                    myCase={myCase}
                    profileImage={profileImage}
                    setProfileImage={setProfileImage}
                    setErrMsg={setErrMsg}
                    setShowToast={setShowToast}
                    setToastText={setToastText}
                    setToastBody={setToastBody}
                  />
                </Col>
              </Row>
              {relationship === "child" && (
                <Row className="h-auto">
                  <Col xs={4}>
                    <Input
                      label="School"
                      type="text"
                      value={school}
                      onUpdate={(e: any) => updateSchool(e)}
                      disabled={myCase ? false : true}
                    />
                  </Col>
                  <Col xs={4}>
                    <Input label="Grade" type="text" value={grade} onUpdate={(e: any) => updateGrade(e)} disabled={myCase ? false : true} />
                  </Col>
                </Row>
              )}
              {(programs.length > 0 || jobs.length > 0) && (
                <Row className="h-auto">
                  {programs.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Program"
                        value={program}
                        options={programs}
                        onUpdate={(e: any) => updateProgram(e)}
                        disabled={isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                  {levels.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Program Level"
                        value={level}
                        options={levels}
                        onUpdate={(e: any) => updateLevel(e)}
                        disabled={isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                  {jobs.length > 0 && (
                    <Col xs={4}>
                      <SelectObjectTypeahead
                        label="Job"
                        value={job}
                        options={jobs}
                        onUpdate={(e: any) => updateJob(e)}
                        disabled={isBanned ? true : myCase ? false : true}
                      />
                    </Col>
                  )}
                </Row>
              )}

              {rooms.length > 0 && beds.length > 0 && (
                <Row className="h-auto">
                  <Col xs={4}>
                    <SelectObject
                      label="Room"
                      value={room}
                      options={rooms}
                      onUpdate={(e: any) => updateRoom(e)}
                      disabled={isBanned ? true : myCase ? false : true}
                    />
                  </Col>
                  <Col xs={4}>
                    <SelectObject
                      label="Bed"
                      value={bed}
                      options={filteredBeds}
                      onUpdate={(e: any) => updateBed(e)}
                      disabled={room ? (myCase ? false : true) : true}
                      initialOption="Vacant"
                    />
                  </Col>
                </Row>
              )}
            </Stack>
          </Stack>
          {user?.customfields?.length > 0 && (
            <Row className="h-auto">
              {user?.customfields?.map((custom: any) => (
                <CustomField
                  fieldValueId={custom?.field_value_id}
                  fieldId={custom?.field_id}
                  label={custom?.label}
                  type={custom?.type}
                  val={custom?.value}
                  setCustom={setCustom}
                  enableSave={enableSave}
                />
              ))}
            </Row>
          )}
          <Row className="h-auto">
            <Col xs={4} className="text-start">
              <Stack direction="horizontal" gap={3}>
                {(auth?.profile?.role === "org_admin" || auth?.profile?.role === "admin") && (
                  <Button className="app-danger-color-inverse button" onClick={() => onClickBan()}>
                    {isBanned ? "Unban" : "Ban"}
                  </Button>
                )}
                <small className="text-muted">{user?.number_infractions + " Infraction(s)"}</small>
              </Stack>
            </Col>
          </Row>
        </div>
      )}
    </>
  );
};

export default EditMember;
